'use client'
import { motion,  } from "motion/react";
import { TIndustriesConfig } from "@/app/config";
import { TrendingUp, Play, Music, Tv, ArrowRight } from "lucide-react";
import Link from "next/link";
import React, { useRef } from "react";

const marketCards = [
  {
    
    icon: Play,
    
  },
  {
    
    icon: Music,
    
  },
  {
    
    icon: Music,
    

    
  },
  {
    
    icon: Tv,
  
  },
];

export default function IndustriesHeroSection({config}:{config:TIndustriesConfig['hero']}) {
  const ref = useRef(null);
  // const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  // const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.8]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15
      }
    }
  };

  const iconVariants = {
    hover: {
      rotate: 360,
      scale: 1.2,
      transition: { duration: 0.6 }
    }
  };

  return (
    <motion.section 
      ref={ref}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 relative overflow-hidden"
      
    >
      {/* Animated Background Elements */}
      <motion.div 
        className="absolute top-10 left-10 w-20 h-20 bg-yellow-400 rounded-full opacity-20 blur-xl"
        variants={{
          animate: {
            x: [0, 30, 0],
            y: [0, -20, 0],
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
            transition: {
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }
          }
        }}
        animate="animate"
      />
      <motion.div 
        className="absolute top-1/3 right-20 w-32 h-32 bg-pink-500 rounded-full opacity-15 blur-2xl"
        variants={{
          animate: {
            x: [0, 30, 0],
            y: [0, -20, 0],
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
            transition: {
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }
          }
        }}
        animate="animate"
        style={{ animationDelay: "5s" }}
      />
      <motion.div 
        className="absolute bottom-20 right-1/4 w-24 h-24 bg-purple-500 rounded-full opacity-20 blur-xl"
        variants={{
          animate: {
            x: [0, 30, 0],
            y: [0, -20, 0],
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
            transition: {
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }
          }
        }}
        animate="animate"
        style={{ animationDelay: "10s" }}
      />

      {/* Floating particles */}
      {Array.from({ length: 20 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-white rounded-full opacity-30"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.8, 0.3],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut"
          }}
        />
      ))}
      
      <motion.div 
        className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-16 lg:py-24"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Column - Content */}
          <motion.div 
            className="space-y-8"
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 60,
                  scale: 0.95
                },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }
                }
              }}
          >
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center px-4 py-2 bg-yellow-400/20 rounded-full border border-yellow-400/30"
              variants={{
                hidden: { opacity: 0, scale: 0.8, y: -20 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    ease: "backOut"
                  }
                },
                hover: {
                  scale: 1.05,
                  boxShadow: "0 0 20px rgba(255, 196, 0, 0.3)",
                  transition: { duration: 0.2 }
                }
              }}
              whileHover="hover"
            >
              <motion.div
                whileHover={{ rotate: 360, scale: 1.2 }}
                transition={{ duration: 0.6 }}
              >
                <TrendingUp className="w-4 h-4 text-yellow-400 mr-2" />
              </motion.div>
              <span className="text-yellow-400 text-sm font-medium">
                {config.tag}
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.div 
              className="space-y-2"
              style={{ perspective: "1000px" }}
            >
              <motion.h1 
                className="text-4xl lg:text-6xl font-bold text-white leading-tight"
                variants={{
                  hidden: { opacity: 0, y: 50, rotateX: -15 },
                  visible: (delay : number) => ({
                    opacity: 1,
                    y: 0,
                    rotateX: 0,
                    transition: {
                      duration: 0.8,
                      delay: delay * 0.2,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }
                  })
                }}
                custom={0}
              >
                {config.title}
              </motion.h1>
              <motion.h1 
                className="text-4xl lg:text-6xl font-bold text-yellow-400 leading-tight"
                variants={{
                  hidden: { opacity: 0, y: 50, rotateX: -15 },
                  visible: (delay : number) => ({
                    opacity: 1,
                    y: 0,
                    rotateX: 0,
                    transition: {
                      duration: 0.8,
                      delay: delay * 0.2,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }
                  })
                }}
                custom={1}
                whileHover={{ 
                  scale: 1.05,
                  textShadow: "0 0 20px rgba(255, 196, 0, 0.5)",
                  transition: { duration: 0.3 }
                }}
              >
                {config.highlighted_title}
              </motion.h1>
              <motion.h1 
                className="text-4xl lg:text-6xl font-bold text-white leading-tight"
                variants={{
                  hidden: { opacity: 0, y: 50, rotateX: -15 },
                  visible: (delay : number) => ({
                    opacity: 1,
                    y: 0,
                    rotateX: 0,
                    transition: {
                      duration: 0.8,
                      delay: delay * 0.2,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }
                  })
                }}
                custom={2}
              >
                {config.sub_title}
              </motion.h1>
            </motion.div>

            {/* Description */}
            <motion.p 
              className="text-gray-300 text-lg lg:text-xl leading-relaxed max-w-lg"
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 60,
                  scale: 0.95
                },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }
                }
              }}
            >
              {config.description}
            </motion.p>

            {/* CTA Button */}
            <motion.div variants={{
                    hidden: { 
                      opacity: 0, 
                      y: 60,
                      scale: 0.95
                    },
                    visible: { 
                      opacity: 1, 
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.7,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }
                    }
                  }}>
              <Link href={config.button.href} target="_blank" className="flex gap-4">
                <motion.button 
                  className="bg-yellow-400 hover:bg-yellow-500 text-black px-8 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                  variants={ {
                    hidden: { opacity: 0, scale: 0.9 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        ease: "backOut"
                      }
                    },
                    hover: {
                      scale: 1.05,
                      boxShadow: "0 15px 30px rgba(255, 196, 0, 0.4)",
                      transition: { duration: 0.2 }
                    },
                    tap: { scale: 0.98 }
                  }}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <span>{config.button.label}</span>
                  <motion.div
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.div>
                </motion.button>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div 
              className="flex flex-wrap gap-8 pt-4"
              variants={containerVariants}
            >
              {config.stats.map((stat, index) => (
                <motion.div 
                  key={index} 
                  className="text-center"
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: (index: number) => ({
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        delay: index * 0.1 + 0.8,
                        ease: "easeOut"
                      }
                    }),
                    hover: {
                      scale: 1.1,
                      transition: { duration: 0.2 }
                    }
                  }}
                  custom={index}
                  whileHover="hover"
                >
                  <motion.div 
                    className={`text-2xl lg:text-3xl font-bold ${stat.color}`}
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.5,
                      ease: "easeInOut"
                    }}
                  >
                    {stat.value}
                  </motion.div>
                  <div className="text-gray-400 text-sm font-medium">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Market Cards */}
          <motion.div 
            className="grid grid-cols-2 gap-4 lg:gap-6"
            style={{ perspective: "1000px" }}
            variants={containerVariants}
          >
            {config.marketCards.map((card, index) => {
              const Icons = marketCards[index].icon
              return(
                <motion.div
                  key={index}
                  className={`${card.bgColor} ${card.borderColor} rounded-xl p-6 text-white shadow-2xl cursor-pointer`}
                  variants={ {
                    hidden: { 
                      opacity: 0, 
                      scale: 0.8,
                      rotateY: -30,
                      z: -100
                    },
                    visible: (index: number) => ({
                      opacity: 1,
                      scale: 1,
                      rotateY: 0,
                      z: 0,
                      transition: {
                        duration: 0.6,
                        delay: index * 0.1 + 0.5,
                        ease: "backOut",
                        type: "spring",
                        damping: 20
                      }
                    }),
                    hover: {
                      scale: 1.08,
                      rotateY: 5,
                      z: 50,
                      boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
                      transition: {
                        duration: 0.3,
                        ease: "easeOut"
                      }
                    }
                  }}
                  custom={index}
                  whileHover="hover"
                  whileTap={{ scale: 0.95 }}
                >
                  {/* Card Icon */}
                  <div className="flex justify-between items-start mb-4">
                    <motion.div 
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.iconBgColor}`}
                      variants={iconVariants}
                      whileHover="hover"
                    >
                      <Icons className="w-6 h-6 text-white" />
                    </motion.div>
                    <motion.div 
                      className="text-right"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.8, duration: 0.5 }}
                    >
                      <div className="text-sm font-medium opacity-90">
                        {card.growth}
                      </div>
                    </motion.div>
                  </div>

                  {/* Card Content */}
                  <motion.div 
                    className="space-y-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.9, duration: 0.5 }}
                  >
                    <h3 className="text-white font-semibold text-sm leading-tight">
                      {card.title}
                    </h3>
                    <motion.div 
                      className="text-2xl font-bold text-white"
                      animate={{
                        scale: [1, 1.02, 1],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.5,
                        ease: "easeInOut"
                      }}
                    >
                      {card.value}
                    </motion.div>
                  </motion.div>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </motion.div>
    </motion.section>
  );
}